import React from 'react';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import {
  clientAPI,
  driverAPI,
  helperAPI,
  settingsAPI,
  staffAPI,
  vehicleAPI,
} from '../services/api';
import type { EntityType } from './documentTypes';

export interface EntityUploadConfig {
  key: EntityType;
  label: string;
  plural: string;
  description: string;
  icon: React.ReactElement;
  profilePath?: (id: string) => string;
  listPath?: string;
  loadOptions: () => Promise<{ _id: string; label: string; sublabel?: string }[]>;
}

function mapRows(
  rows: Record<string, unknown>[],
  labelKeys: string[],
  sublabelKeys: string[] = [],
) {
  return rows.map((row) => {
    const label = labelKeys.map((k) => row[k]).find(Boolean);
    const sublabel = sublabelKeys.map((k) => row[k]).find(Boolean);
    return {
      _id: String(row._id),
      label: String(label || 'Record'),
      sublabel: sublabel ? String(sublabel) : undefined,
    };
  });
}

export const DOCUMENT_ENTITY_CONFIG: EntityUploadConfig[] = [
  {
    key: 'vehicle',
    label: 'Vehicle',
    plural: 'Vehicles',
    description: 'RC, fitness, insurance, PUC, permits, tax, FASTag, supportive docs',
    icon: React.createElement(DirectionsCarIcon),
    profilePath: (id) => `/vehicles/${id}`,
    listPath: '/vehicles',
    loadOptions: async () => {
      const res = await vehicleAPI.getAll();
      return mapRows(res.data || [], ['vehicleNumber', 'vehicleModel'], ['vehicleType']);
    },
  },
  {
    key: 'driver',
    label: 'Driver',
    plural: 'Drivers',
    description: 'License, ID, medical',
    icon: React.createElement(PersonIcon),
    profilePath: (id) => `/drivers/${id}`,
    listPath: '/drivers',
    loadOptions: async () => {
      const res = await driverAPI.getAll();
      return mapRows(res.data || [], ['driverName', 'name'], ['driverCode', 'mobile']);
    },
  },
  {
    key: 'helper',
    label: 'Helper',
    plural: 'Helpers',
    description: 'ID proof, contract, training',
    icon: React.createElement(EngineeringIcon),
    listPath: '/helpers',
    loadOptions: async () => {
      const res = await helperAPI.getAll();
      return mapRows(res.data || [], ['helperName', 'name'], ['helperCode', 'mobile']);
    },
  },
  {
    key: 'staff',
    label: 'Staff',
    plural: 'Staff',
    description: 'ID, offer letter, contract',
    icon: React.createElement(BadgeIcon),
    listPath: '/staff',
    loadOptions: async () => {
      const res = await staffAPI.getAll();
      return mapRows(res.data || [], ['name'], ['designation', 'mobile']);
    },
  },
  {
    key: 'client',
    label: 'Client',
    plural: 'Clients',
    description: 'Agreement, GST, PAN',
    icon: React.createElement(BusinessIcon),
    listPath: '/clients',
    loadOptions: async () => {
      const res = await clientAPI.getAll();
      return mapRows(res.data || [], ['clientName', 'companyFullName'], ['mobile', 'city']);
    },
  },
  {
    key: 'company',
    label: 'Company',
    plural: 'Company',
    description: 'GST, PAN, business license',
    icon: React.createElement(DomainIcon),
    loadOptions: async () => {
      const res = await settingsAPI.get();
      const company = res.data;
      return [{
        _id: String(company._id),
        label: String(company.companyName || 'Company'),
        sublabel: company.gstNumber ? `GST: ${company.gstNumber}` : undefined,
      }];
    },
  },
];

export const DOCUMENT_ENTITY_MAP = Object.fromEntries(
  DOCUMENT_ENTITY_CONFIG.map((c) => [c.key, c]),
) as Record<EntityType, EntityUploadConfig>;

export async function buildEntityLabelMap(): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  await Promise.all(
    DOCUMENT_ENTITY_CONFIG.map(async (config) => {
      try {
        const options = await config.loadOptions();
        options.forEach((opt) => {
          map[`${config.key}:${opt._id}`] = opt.label;
        });
      } catch {
        /* skip failed entity list */
      }
    }),
  );
  return map;
}
